import { R } from '@/features/booking/resources/Texts.resx';
import { AiNurseReturn } from '@/features/booking/widgets/describe-problem/model/useAiNurse';
import {
    Button,
    Card,
    HorizontalContainer,
    Textarea,
    VerticalContainer,
} from '@/components';
import { AiStarIcon, AttachmentIcon } from '@/components/Icons';
import {
    bookingDraftAtom,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { Log } from '@/shared/Log';
import { useAttachments } from '@/features/booking/widgets/describe-problem/model/useAttachments';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { AttachmentsDropzone } from '@/features/booking/widgets/describe-problem/ui/AttachmentsDropzone';

interface AiNurseProps {
    aiNurse: AiNurseReturn;
    onSubmitAndContinue: () => void;
}

const ITEM_SEPARATOR = '\n\n';

export const AiNurse = ({ aiNurse, onSubmitAndContinue }: AiNurseProps) => {
    const draftState = useAtomValue(bookingDraftAtom);
    const updateDraftState = useSetAtom(updateDraftAtom);

    const questions = useMemo(
        () =>
            (draftState?.aiNurseQuestion ?? '')
                .split(ITEM_SEPARATOR)
                .map((question) => question.trim())
                .filter(Boolean),
        [draftState?.aiNurseQuestion],
    );

    const answers = useMemo(() => {
        const stored = (draftState?.aiNurseAnswer ?? '').split(ITEM_SEPARATOR);
        return questions.map((_, index) => stored[index] ?? '');
    }, [draftState?.aiNurseAnswer, questions]);

    // Answers are stored in a single string separated by two blank lines, so blank
    // lines within an answer are collapsed to keep the question/answer pairing intact.
    const normalizeAnswer = (value: string) =>
        value.replace(/\n{2,}/g, '\n').trim();

    const updateAnswer = (index: number, value: string) => {
        const updatedAnswers = [...answers];
        updatedAnswers[index] = normalizeAnswer(value);
        updateDraftState({
            aiNurseAnswer: updatedAnswers.join(ITEM_SEPARATOR),
        });
    };

    const handleSubmit = () => {
        aiNurse.submitAnswer(concatenatedMessage);
        onSubmitAndContinue();
    };

    const handleSaveAndContinue = () => {
        aiNurse.goToOverview();
    };

    const handleEditMessage = () => {
        Log.info('AI Nurse: user went back to edit message');
        aiNurse.submitAnswer(concatenatedMessage);
    };

    const concatenatedMessage = useMemo(() => {
        const questionsAndAnswers = questions
            .map((question, index) => `**${question}**\n${answers[index]}`)
            .join(ITEM_SEPARATOR);

        return [draftState?.message ?? '', questionsAndAnswers]
            .filter(Boolean)
            .join(ITEM_SEPARATOR);
    }, [draftState?.message, questions, answers]);

    const { files } = useAttachments({
        onChange: () => {},
    });

    const updateAttachmentUids = (uids: string[]) => {
        updateDraftState({ attachmentUids: uids.join(',') });
    };

    return (
        <Card alwaysCard={false}>
            <h2 className="mb-4">{aiNurse.getHeader()}</h2>
            <div>{aiNurse.getDescription()}</div>

            {aiNurse.showQuestion && (
                <VerticalContainer spacing="lg">
                    <div
                        className="rounded-[28px] p-0.5"
                        style={{
                            background:
                                'linear-gradient(to right, var(--color-secondary-blue-dark), var(--color-secondary-green), var(--color-action-orange), var(--color-secondary-purple))',
                        }}
                    >
                        <HorizontalContainer className="items-center rounded-[26px] bg-(--color-white) p-4">
                            <AiStarIcon />
                            <span className="text-sm text-(--color-text-primary)">
                                {R.AiNurseQuestionsSubheader}
                            </span>
                        </HorizontalContainer>
                    </div>

                    <VerticalContainer spacing="lg">
                        {questions.map((question, index) => (
                            <FormControl
                                key={index}
                                id={`answer-${index}`}
                                label={question}
                            >
                                <Textarea
                                    className="min-h-28"
                                    defaultValue={answers[index]}
                                    placeholder={R.AiNurseAnswerPlaceholder}
                                    onBlur={(e) =>
                                        updateAnswer(index, e.target.value)
                                    }
                                />
                            </FormControl>
                        ))}
                    </VerticalContainer>

                    {aiNurse.suggestAttachment && (
                        <AttachmentsDropzone onChange={updateAttachmentUids} />
                    )}

                    <HorizontalContainer
                        spacing="none"
                        className="justify-between"
                    >
                        <Button variant="ghost" onClick={aiNurse.skip}>
                            {R.Skip}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveAndContinue}
                        >
                            {R.AiNurseSaveAndContinue}
                        </Button>
                    </HorizontalContainer>
                </VerticalContainer>
            )}

            {aiNurse.showOverview && (
                <VerticalContainer spacing="lg">
                    <VerticalContainer
                        spacing="lg"
                        className="rounded-2xl bg-(--color-secondary-blue-extralight) p-4"
                    >
                        <VerticalContainer spacing="sm">
                            <div className="text-xl font-bold text-(--color-text-primary)">
                                {draftState?.subject}
                            </div>
                            <div className="wrap-break-word whitespace-pre-wrap">
                                {draftState?.message}
                            </div>
                        </VerticalContainer>

                        {questions.map((question, index) => (
                            <VerticalContainer key={index} spacing="sm">
                                <div className="font-bold text-(--color-text-primary)">
                                    {question}
                                </div>
                                {answers[index] && (
                                    <div className="wrap-break-word whitespace-pre-wrap">
                                        {answers[index]}
                                    </div>
                                )}
                            </VerticalContainer>
                        ))}

                        {files.length > 0 && (
                            <VerticalContainer spacing="none">
                                <HorizontalContainer spacing="xs">
                                    <AttachmentIcon />
                                    {R.Attachments}
                                </HorizontalContainer>
                                <AttachmentsDropzone
                                    className="-mb-4"
                                    onChange={() => {}}
                                    previewOnly
                                />
                            </VerticalContainer>
                        )}
                    </VerticalContainer>

                    <HorizontalContainer
                        spacing="none"
                        className="justify-between"
                    >
                        <Button variant="ghost" onClick={handleEditMessage}>
                            {R.AiNurseEditMessage}
                        </Button>
                        <Button variant="primary" onClick={handleSubmit}>
                            {R.Confirm}
                        </Button>
                    </HorizontalContainer>
                </VerticalContainer>
            )}
        </Card>
    );
};
